import React, { useState, useEffect } from 'react';
import { Dropdown, Button, Form, Alert, InputGroup } from 'react-bootstrap';
import { getAllCountries, getAllStates, getAllCities } from '../../../../../crud/configuration_api/places';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { ChevronRight } from 'react-bootstrap-icons';
import './CreateCompany.scss';

interface CreateCompanyProps {
  index: number;
  moveFunctions: { next: () => void };
  setPayload: (payload: any) => void;
  accountCode: string;
  countries: any[];
  setCountries: React.Dispatch<React.SetStateAction<any[]>>;
  states: any[];
  setStates: React.Dispatch<React.SetStateAction<any[]>>;
  cities: any[];
  setCities: React.Dispatch<React.SetStateAction<any[]>>;
  companyName: string;
  setCompanyName: React.Dispatch<React.SetStateAction<string>>;
  payload: any;
}

const CreateCompany: React.FC<CreateCompanyProps> = ({
  index,
  moveFunctions,
  setPayload,
  accountCode,
  countries,
  setCountries,
  states,
  setStates,
  cities,
  setCities,
  companyName,
  setCompanyName,
  payload
}) => {
  
  const [error, setError] = useState<string | null>(null);

  // Cambiar la inicialización de formData para usar payload si existe
  const storedData = JSON.parse(localStorage.getItem("RegistrationMemory") || "{}")
  const localData = storedData[0] || {}
  const initialFormData = payload || {
    company_name: '',
    company_rut: localData.company_rut || '',
    company_email: localData.company_email || '',
    company_phone: localData.company_phone || '',
    address: localData.address || '',
    state: true,
    plan_id: 6,
    plan_state: false,
    country: localData.country || '',
    estado: localData.estado || '',
    city_id: localData.city_id || '',
    company_password: localData.company_password || '',
    code: accountCode
  }
  const [formData, setFormData] = useState(initialFormData)

  // Sincronizar formData cuando cambie el payload
  useEffect(() => {
    if (payload) setFormData(payload)
  }, [payload])

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);

  const passwordsMatch = formData.company_password === confirmPassword;

  useEffect(() => {
    getAllCountries()
      .then((response) => setCountries(response.data))
      .catch((error) => console.error('Error al obtener países:', error));
  }, []);

  useEffect(() => {
    if (formData.country) {
      getAllStates()
        .then((response) => {
          const filteredStates = response.data.filter(
            (state: any) => state.country_id === parseInt(formData.country)
          );
          setStates(filteredStates);
        })
        .catch((error) => console.error('Error al obtener estados:', error));
    } else {
      setStates([]);
    }
  }, [formData.country]);

  useEffect(() => {
    if (formData.estado) {
      getAllCities()
        .then((response) => {
          const filteredCities = response.data.filter(
            (city: any) => city.state_id === parseInt(formData.estado)
          );
          setCities(filteredCities);
        })
        .catch((error) => console.error('Error al obtener ciudades:', error));
    } else {
      setCities([]);
    }
  }, [formData.estado]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    if (field === 'company_name') setCompanyName(value);
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.company_email || !formData.company_password || !confirmPassword || !formData.company_phone || !formData.country || !formData.estado || !formData.city_id) {
      setError('Todos los campos son requeridos.');
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(formData.company_email)) {
      setError('El correo electrónico debe contener una "@"');
      return false;
    }

    if (passwordTouched && !passwordsMatch) {
      setError('Las contraseñas no coinciden.');
      return false;
    }

    setError(null);
    return true;
  };

  const handleNext = () => {
    if (validateForm()) {
      // Construir address como la unión de country, state y city
      const countryName = countries.find(c => c.country_id.toString() === formData.country)?.country_name || '';
      const stateName = states.find(s => s.state_id.toString() === formData.estado)?.state_name || '';
      const cityName = cities.find(c => c.city_id.toString() === formData.city_id)?.city_name || '';
      const address = `${countryName}, ${stateName}, ${cityName}`;
      // Asegurar que plan_id y plan_state nunca sean null o undefined
      const updatedData = { 
        ...formData, 
        company_name: companyName, 
        code: accountCode, 
        address,
        plan_id: formData.plan_id ?? 6,
        plan_state: formData.plan_state ?? false
      };
      setPayload(updatedData);
      moveFunctions.next();
    }
  };

  return (
    <div style={{borderRadius: ''}}>
      <div className="logo-container-preg">
        <img
          src="/logos/Recurso 40MEDIOPUNTO.png"
          alt="Logo Mediopunto"
          className="registration-logo-preg"
        />
      </div>
      <div className="info-guide-preg" >
        <h2 className="Title-Step-Registration-preg">Crea tu compañía</h2>
        <p>
          <FontAwesomeIcon icon={faInfoCircle} size="sm" /> ¡Hola! Estamos muy contentos de que te unas a Medio Punto. En este primer paso vamos a recopilar la información de tu compañía para completar el registro.
                                                            Por favor, ingresa todos los datos de tu empresa en los campos que aparecen a continuación (recuerda que no pueden quedar espacios en blanco).
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="form-create-company">
        <div className='form-column'>
            <Form.Control
              type="text"
              id="company_name"
              value={companyName}
              onChange={e => handleChange('company_name', e.target.value)}
              required      
              placeholder='Nombre de la compañía'
              className='input-create-company'
              disabled
            />


          <InputGroup>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                id="company_password"
                value={formData.company_password}
                onChange={(e) => handleChange('company_password', e.target.value)}
                onBlur={() => setPasswordTouched(true)}
                required
                placeholder='Contraseña'
                className='input-create-company'
              />
              <Button variant="none" onClick={() => setShowPassword(prev => !prev)}>
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </Button>
          </InputGroup>


          <Form.Control
            type="text"
            id="company_phone"
            value={formData.company_phone}
            onChange={(e) => handleChange('company_phone', e.target.value)}
            required
            placeholder='Teléfono'
            className='input-create-company'
          />
          <Dropdown className='dropdown-create-company'>
              <Dropdown.Toggle variant='none'>{formData.estado ? states.find(s => s.state_id.toString() === formData.estado)?.state_name : 'Seleccione una región'}</Dropdown.Toggle>
              <Dropdown.Menu variant='none'>
                {states.map(state => (
                  <Dropdown.Item key={state.state_id} onClick={() => handleChange('estado', state.state_id.toString())}>
                    {state.state_name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
          </Dropdown>
        </div>





        <div className='form-column'>
            <Form.Control
              type="email"
              id="company_email"
              value={formData.company_email}
              onChange={(e) => handleChange('company_email', e.target.value)}
              required
              placeholder='Correo electrónico'
              className='input-create-company'
            />


            <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(null); }}
                  placeholder='Confirmar Contraseña'
                  required
                  className='input-create-company'
                />
                <Button variant="none" onClick={() => setShowConfirmPassword(prev => !prev)}>
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
              {!passwordsMatch && confirmPassword.length > 0 && (
                <Form.Text className="text-danger">Las contraseñas no coinciden.</Form.Text>
            )}
            <Dropdown className='dropdown-create-company'>
              <Dropdown.Toggle variant='none'>{formData.country ? countries.find(c => c.country_id.toString() === formData.country)?.country_name : 'Seleccione un país'}</Dropdown.Toggle>
              <Dropdown.Menu variant='none'>
                {countries.map(country => (
                  <Dropdown.Item key={country.country_id} onClick={() => handleChange('country', country.country_id.toString())}>
                    {country.country_name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className='dropdown-create-company'>
              <Dropdown.Toggle variant='none'>{formData.city_id ? cities.find(c => c.city_id.toString() === formData.city_id)?.city_name : 'Seleccione una ciudad'}</Dropdown.Toggle>
              <Dropdown.Menu variant='none'>
                {cities.map(city => (
                  <Dropdown.Item key={city.city_id} onClick={() => handleChange('city_id', city.city_id.toString())}>
                    {city.city_name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
        </div>
      </div>

      <Button className='NextStep_Registration-preg' variant='none' onClick={handleNext}>
        Siguiente <ChevronRight/> 
      </Button>
    </div>
  );
};

export default CreateCompany;
